import { createElement, useState } from 'react';
import { GripVertical, Eye, EyeOff } from 'lucide-react';
import { QUESTION_TYPE_ICONS } from '.';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

// Define drag type constant
const QUESTION_ITEM = 'question';

// Draggable Question Item Component
function QuestionItem({ question, index, moveQuestion, toggleVisibility }) {
    const [{ isDragging }, drag] = useDrag({
        type: QUESTION_ITEM,
        item: { index },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const [, drop] = useDrop({
        accept: QUESTION_ITEM,
        hover: (draggedItem) => {
            if (draggedItem.index !== index) {
                moveQuestion(draggedItem.index, index);
                draggedItem.index = index;
            }
        },
    });

    return (
        <div
            ref={(node) => drag(drop(node))}
            className={`flex justify-between items-center gap-4 p-3 bg-white border border-gray-100 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 ${isDragging ? 'opacity-50 rotate-2' : ''}`}
        >
            <div className="flex items-center gap-3 min-w-0 flex-1">
                <GripVertical className="w-4 h-4 text-gray-400 cursor-move shrink-0 hover:text-gray-600 transition-colors" />
                <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className={`
                        w-8 h-8 rounded-lg flex items-center justify-center shrink-0 shadow-sm
                        ${QUESTION_TYPE_ICONS[question.type].bgColor}
                    `}>
                        {createElement(QUESTION_TYPE_ICONS[question.type].icon, {
                            size: 16,
                            className: QUESTION_TYPE_ICONS[question.type].iconColor
                        })}
                    </div>
                    <span className="text-sm font-medium text-gray-800 min-w-0 flex-1 leading-snug break-words">
                        {question.text}
                    </span>
                </div>
            </div>
            <div className='shrink-0'>
                <button
                    onClick={() => toggleVisibility(index)}
                    className={`p-2 rounded-lg transition-all duration-200 hover:shadow-md focus:outline-none ${
                        question.visible 
                            ? 'bg-blue-50 text-blue-600 hover:bg-blue-100' 
                            : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                    }`}
                    title={question.visible ? 'Hide column' : 'Show column'}
                >
                    {question.visible ? (
                        <Eye className="w-5 h-5" />
                    ) : (
                        <EyeOff className="w-5 h-5" />
                    )}
                </button>
            </div>
        </div>
    );
}

export function TableSettings({
    questions = [],
    onVisibilityChange,
    onOrderChange,
    onApply,
    onCancel
}) {
    // Keep track of local state for visibility and order
    const [localQuestions, setLocalQuestions] = useState(
        questions.map(q => ({ ...q, visible: q.visible ?? true }))
    );

    const moveQuestion = (fromIndex, toIndex) => {
        setLocalQuestions(prevQuestions => {
            const newQuestions = [...prevQuestions];
            const [movedItem] = newQuestions.splice(fromIndex, 1);
            newQuestions.splice(toIndex, 0, movedItem);
            return newQuestions;
        });
    };

    // Toggle visibility for a question
    const toggleVisibility = (index) => {
        setLocalQuestions(prev => prev.map((q, i) =>
            i === index ? { ...q, visible: !q.visible } : q
        ));
    };

    // Handle apply changes
    const handleApply = () => {
        onApply?.(localQuestions);
    };

    return (
        <div className="p-2">
            {/* Description */}
            <div className="mb-6">
                <p className="text-sm text-gray-600">Drag to reorder columns and click the eye icon to toggle visibility</p>
            </div>

            <DndProvider backend={HTML5Backend}>
                <div className="space-y-3 max-h-[65vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 pr-2">
                    {/* Questions List */}
                    {localQuestions.map((question, index) => (
                        <QuestionItem
                            key={question.id}
                            question={question}
                            index={index}
                            moveQuestion={moveQuestion}
                            toggleVisibility={toggleVisibility}
                        />
                    ))}
                </div>
            </DndProvider>

            {/* Footer Actions */}
            <div className="mt-6 flex justify-end gap-3">
                <button
                    onClick={onCancel}
                    className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-xl hover:bg-gray-100 hover:border-gray-300 transition-colors duration-200"
                >
                    Cancel
                </button>
                <button
                    onClick={handleApply}
                    className="px-6 py-2.5 text-sm font-medium text-white bg-gray-900 rounded-xl hover:bg-black shadow-sm hover:shadow-md transition-all duration-200"
                >
                    Apply Changes
                </button>
            </div>
        </div>
    );
}
